/** @jsxImportSource @emotion/react */

import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { X } from 'lucide-react';
import { useCallback } from 'react';
import { components } from 'react-select';
import { theme } from 'styles/theme/theme';
import CircleChevronIcon from 'ud-ui/components/icon/circle-chevron-icon';
import UDText from 'ud-ui/components/text';
import * as S from './styles';
import { SelectOption } from './types';

const NoOptions = () => {
  return (
    <div className="flex-center flex-col p-8">
      <UDText type="subhead" className="text-surfaceRomanSilver30 mb-2">
        <Trans>Список пуст</Trans>
      </UDText>
    </div>
  );
};

export const DropDown = (props: any) => {
  const { menuIsOpen } = props.selectProps;
  return (
    <div
      id="dropdown-indicator"
      ref={props.innerRef}
      {...props.innerProps}
      className="flex-center p-2"
      style={{
        transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.25s ease',
      }}
    >
      <CircleChevronIcon width={20} height={20} />
    </div>
  );
};

export const DropDownWhite = (props: any) => {
  return (
    <div ref={props.innerRef} {...props.innerProps} className="flex-center p-2">
      <CircleChevronIcon width={20} height={20} color="white" />
    </div>
  );
};

const Option = (props: any) => {
  const { description, count } = (props.data ?? {}) as Pick<SelectOption, 'description' | 'count'>;
  const className = classNames('flex items-center justify-between gap-3 px-4', props.isMulti || description ? 'py-1' : 'py-2');

  return (
    <S.Option {...props.innerProps} className={className} aria-disabled={props.isDisabled || undefined}>
      <div className="min-w-0">
        <UDText type="subhead" className={props.isDisabled ? 'text-inkDisabled' : undefined}>
          {props.children}
        </UDText>
        {description && (
          <UDText type="caption-1" className="text-inkMuted">
            {description}
          </UDText>
        )}
      </div>
      {(count !== undefined || props.isMulti) && (
        <div className="flex shrink-0 items-center gap-3">
          {count !== undefined && (
            <UDText type="subhead" className="text-inkMuted tabular-nums">
              {count}
            </UDText>
          )}
          {props.isMulti && <S.OptionCheckbox data-testid="select-option-checkbox" data-checked={props.isSelected} />}
        </div>
      )}
    </S.Option>
  );
};

const MultiValue = (props: any) => {
  return (
    <S.MultiValue>
      <UDText
        type="subhead"
        style={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {props.children}
      </UDText>
    </S.MultiValue>
  );
};

const SingleValue = (props: any) => {
  return (
    <div>
      <UDText type="subhead">{props.children}</UDText>
    </div>
  );
};

const ClearIndicator = (props: any) => {
  return (
    <div ref={props.innerRef} {...props.innerProps} onClick={props.clearValue} className="flex-center p-2">
      <X size={21} color={theme.colors.SurfaceRomanSilver30} />
    </div>
  );
};

const IndicatorsContainer = (props: any) => {
  return (
    <div {...props.innerProps} className="align-item-center ml-auto flex flex-row px-2">
      {props.children}
    </div>
  );
};

const ValueContainer = (props: any) => {
  return (
    <S.ValueContainer id="value-container" className="flex flex-row items-center" {...props.innerProps}>
      <div className="text subhead flex flex-row items-center" style={{ color: 'black' }}>
        {props.children}
      </div>
    </S.ValueContainer>
  );
};

const Control = (props: any) => {
  return (
    <S.Control
      ref={props.innerRef}
      data-error={props.selectProps.hasError}
      {...props.innerProps}
      className={classNames('flex h-9 flex-row pl-4', {
        disabled: props.isDisabled,
        focused: props.isFocused,
        multi: props.isMulti,
        opened: props.menuIsOpen,
      })}
    >
      {props.children}
    </S.Control>
  );
};

const SelectContainer = (props: any) => {
  return (
    <S.SelectContainer {...props.innerProps} className={classNames(props.className, 'shrink-0')}>
      {props.children}
    </S.SelectContainer>
  );
};

const Placeholder = (props: any) => {
  return (
    <UDText
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: '18px',
        // right: '12px',
      }}
      type="subhead"
      className="text-surfaceRomanSilver30"
      {...props.innerProps}
    >
      {props.children}
    </UDText>
  );
};

/**
 * `maxHeight` — высота, которую react-select посчитал под место у края экрана (не больше
 * `maxMenuHeight`, по умолчанию 300). Без неё список, развёрнутый вверх у нижнего поля низкого окна,
 * уходил за верх экрана (IMGM-1623). 300px в `S.MenuList` — запас на случай, если пропса нет.
 */
const MenuList = (props: any) => {
  return (
    <S.MenuList ref={props.innerRef} {...props.innerProps} style={{ maxHeight: props.maxHeight }}>
      {props.children}
    </S.MenuList>
  );
};

const MenuInfo = (props: any) => {
  const { t } = useLingui();
  const selectedOptions = props.getValue();
  const selectedCount = selectedOptions.length;
  const allSelected = selectedCount === props.options.length;
  const buttonText = allSelected
    ? t`снять все`
    : props.hasValue
      ? t`Выбрать все`
      : t({ message: 'все', comment: 'Кнопка «выбрать все опции», когда ничего не выбрано' });
  const onSelectAll = useCallback(() => {
    if (allSelected) {
      return props.clearValue();
    }
    return props.setValue(props.options, 'select-option');
  }, [allSelected, props]);
  return (
    <S.MenuInfo className="flex items-center justify-between px-4 py-2">
      <UDText type="subhead" className="text-surfaceRomanSilver30">
        <Trans>Выбрано {selectedCount}</Trans>
      </UDText>
      <UDText type="subhead" onClick={onSelectAll} className="text-accentNaplesYellow cursor-pointer">
        {buttonText}
      </UDText>
    </S.MenuInfo>
  );
};

const Menu = (props: any) => {
  return (
    <components.Menu {...props} {...props.innerProps} className={classNames(props.className, 'shadow-custom-4', 'mt-0')}>
      {props.isMulti && <MenuInfo {...props} />}
      {props.children}
    </components.Menu>
  );
};

const overrideSelectComponents = {
  NoOptionsMessage: NoOptions,
  DropdownIndicator: DropDown,
  IndicatorSeparator: null,
  Option: Option,
  MultiValue: MultiValue,
  ClearIndicator: ClearIndicator,
  IndicatorsContainer: IndicatorsContainer,
  ValueContainer: ValueContainer,
  Control: Control,
  SelectContainer: SelectContainer,
  Placeholder: Placeholder,
  Menu: Menu,
  MenuList: MenuList,
  SingleValue: SingleValue,
  Input: (props: any) => <components.Input {...props} isHidden={false} />,
};

export default overrideSelectComponents;
